/**
 * ReportResults Component
 * Visualização de resultados de relatório
 */

import React from 'react';
import { Download, BarChart3, Table as TableIcon, Info, Clock, Database, Zap } from 'lucide-react';
import type { ReportResult } from '@/services/reports-service';
import { parseDbTimestamp } from '@/utils/datetime';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/utils/cn';

interface ReportResultsProps {
  result: ReportResult;
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: 'csv' | 'json') => void;
}

const MAX_ROWS = 100;

const formatDate = (date: string) => {
  const parsed = parseDbTimestamp(date);
  if (!parsed) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(parsed);
};

const formatExecutionTime = (ms: number) => (ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`);

const EmptyText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="py-8 text-center text-sm text-muted-foreground">{children}</div>
);

const JsonBlock: React.FC<{ title: string; value: unknown; titleClassName?: string }> = ({ title, value, titleClassName }) => (
  <div className="min-w-0 rounded-lg bg-muted/50 p-4">
    <h4 className={cn('mb-2 font-medium', titleClassName)}>{title}</h4>
    <pre className="overflow-auto whitespace-pre-wrap break-words text-sm text-muted-foreground">
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

const InfoTile: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0 rounded-lg border bg-card p-4">
    <p className="mb-1 text-sm text-muted-foreground">{label}</p>
    {children}
  </div>
);

const Aggregations: React.FC<{ aggregations?: Record<string, unknown> }> = ({ aggregations }) => {
  if (!aggregations) return <EmptyText>Sem agregações disponíveis</EmptyText>;

  return (
    <div className="flex flex-col gap-4">
      {Object.entries(aggregations).map(([key, value]) => (
        <JsonBlock key={key} title={key.replace(/_/g, ' ')} value={value} titleClassName="capitalize" />
      ))}
    </div>
  );
};

const DataTab: React.FC<{ result: ReportResult }> = ({ result }) => {
  if (!result.data || result.data.length === 0) return <EmptyText>Nenhum registro encontrado</EmptyText>;

  const columns = Object.keys(result.data[0]);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="max-h-[500px] min-w-0 overflow-auto rounded-lg border">
        <Table className="min-w-max">
          <TableHeader className="sticky top-0 bg-muted">
            <TableRow>
              {columns.map(col => (
                <TableHead key={col}>{col}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.data.slice(0, MAX_ROWS).map((row, idx) => (
              <TableRow key={idx}>
                {columns.map(col => (
                  <TableCell key={col}>
                    {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {result.data.length > MAX_ROWS && (
        <div className="rounded-lg border bg-muted/50 p-3 text-center text-sm text-muted-foreground">
          Exibindo apenas os primeiros 100 registros de {result.metadata.totalRecords}.
          Exporte para ver todos.
        </div>
      )}
    </div>
  );
};

const InfoTab: React.FC<{ result: ReportResult }> = ({ result }) => (
  <div className="flex flex-col gap-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <InfoTile label="ID da Execução">
        <p className="break-all font-mono text-sm">{result.executionId}</p>
      </InfoTile>
      <InfoTile label="Total de Registros">
        <p className="text-2xl font-semibold tabular-nums">{result.metadata.totalRecords}</p>
      </InfoTile>
      <InfoTile label="Tempo de Execução">
        <p className="text-2xl font-semibold tabular-nums">{formatExecutionTime(result.metadata.executionTime)}</p>
      </InfoTile>
      <InfoTile label="Gerado em">
        <p className="text-sm tabular-nums">{formatDate(result.metadata.generatedAt)}</p>
      </InfoTile>
    </div>

    {result.metadata.fromCache && (
      <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-4">
        <Database className="size-5 shrink-0 text-primary" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium">Resultado do Cache</p>
          <p className="text-xs text-muted-foreground">Este relatório foi recuperado do cache para maior velocidade</p>
        </div>
      </div>
    )}

    {result.metadata.parameters && Object.keys(result.metadata.parameters).length > 0 && (
      <JsonBlock title="Parâmetros Utilizados" value={result.metadata.parameters} />
    )}
  </div>
);

export const ReportResults: React.FC<ReportResultsProps> = ({
  result,
  isOpen,
  onClose,
  onExport
}) => (
  <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
    <DialogContent size="2xl" fullScreenOnMobile className="flex flex-col sm:max-h-[90vh]">
      <DialogHeader className="pr-8">
        <DialogTitle className="truncate">Resultados: {result.reportCode}</DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1 tabular-nums">
              <Clock className="size-4" aria-hidden="true" />
              {formatDate(result.metadata.generatedAt)}
            </span>
            <span>{result.metadata.totalRecords} registros</span>
            <span className="tabular-nums">{result.metadata.executionTime}ms</span>
            {result.metadata.fromCache && (
              <Badge variant="secondary">
                <Zap />
                Do cache
              </Badge>
            )}
          </div>
        </DialogDescription>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onExport('csv')}>
            <Download className="size-4" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => onExport('json')}>
            <Download className="size-4" />
            JSON
          </Button>
        </div>
      </DialogHeader>

      <Tabs defaultValue="aggregations" className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="shrink-0 overflow-x-auto border-b">
          <TabsList variant="line" className="h-auto min-w-max justify-start">
            <TabsTrigger value="aggregations" className="gap-2 px-3 py-2.5">
              <BarChart3 className="size-4" />
              Agregações
            </TabsTrigger>
            <TabsTrigger value="data" className="gap-2 px-3 py-2.5">
              <TableIcon className="size-4" />
              Dados
            </TabsTrigger>
            <TabsTrigger value="info" className="gap-2 px-3 py-2.5">
              <Info className="size-4" />
              Info
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="aggregations" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <Aggregations aggregations={result.aggregations} />
        </TabsContent>
        <TabsContent value="data" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <DataTab result={result} />
        </TabsContent>
        <TabsContent value="info" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <InfoTab result={result} />
        </TabsContent>
      </Tabs>
    </DialogContent>
  </Dialog>
);
